import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, DatabaseBackup, ShieldCheck, Loader2, FileArchive } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { apiError, formatStampUK, formatBytes } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const stamp = formatStampUK;
const lastMonth = () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 7); };

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

function PackageCard({ storeId }) {
  const [month, setMonth] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ month, ...(storeId ? { store_id: storeId } : {}) });
      const r = await api.get(`/reports/accountant-package?${params}`, { responseType: "blob" });
      saveBlob(r.data, `grainmill-accountant-package-${month}.zip`);
      toast.success(`Package ready: ${r.headers["x-package-documents"] || 0} document files included`);
    } catch (err) {
      toast.error(apiError(err, "Could not build the package"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-navy/5 shadow-none" data-testid="accountant-package-card">
      <CardContent className="p-4 space-y-3">
        <h2 className="font-semibold text-navy flex items-center gap-2"><FileArchive className="w-5 h-5 text-gold" /> Monthly accountant package</h2>
        <p className="text-xs text-navy/60">One ZIP file per month: every purchase and expense, totals, the approval history and the original invoice and receipt files. Save it to OneDrive from your phone's Files / share menu.</p>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="gm-input" data-testid="package-month-input" />
        <Button onClick={download} disabled={busy || !month} className="w-full min-h-[48px] bg-gold text-navy hover:bg-gold-light" data-testid="download-package-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download package</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function RunRow({ r }) {
  const d = r.details || {};
  const label = r.kind === "db_backup" ? "Nightly backup" : r.kind === "restore_drill" ? "Restore drill" : "Accountant package";
  const info = r.kind === "restore_drill"
    ? (d.restored_counts ? `${Object.values(d.restored_counts).reduce((a, b) => a + b, 0)} rows restored, counts ${d.counts_match ? "match" : "DO NOT match"}${d.document_check ? `, document ${d.document_check.document_id} retrieved` : ""}` : d.error || "")
    : r.kind === "db_backup" ? (d.bytes ? `${d.pg_dump || d.format || "export"}, ${formatBytes(d.bytes)}, ${d.encryption}${d.encrypted_sha256 ? `, SHA-256 ${d.encrypted_sha256.slice(0, 12)}` : ""}` : d.error || "")
      : `${d.month}: ${d.purchases} purchases, ${d.expenses} expenses, ${d.document_files} files`;
  return (
    <li className="py-2 border-b border-navy/5 last:border-0" data-testid={`run-${r.id}`}>
      <div className="flex justify-between gap-2 text-sm"><span className="text-navy font-medium">{label}</span>
        <span className={r.status === "success" ? "text-emerald-600" : r.status === "failed" ? "text-red-600" : "text-amber-600"}>{r.status}</span></div>
      <p className="text-xs text-navy/50">{stamp(r.finished_at || r.created_at)} · {info}</p>
    </li>
  );
}

function BackupCard({ isOwner }) {
  const [runs, setRuns] = useState([]);
  const [store, setStore] = useState(null);
  const [health, setHealth] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/backups").then((r) => { setRuns(r.data.runs); setStore(r.data); }).catch(() => {});
    api.get("/admin/backup-status").then((r) => setHealth(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const trigger = async (url, msg) => {
    try { await api.post(url); toast.success(msg); setTimeout(load, 4000); } catch (err) { toast.error(apiError(err, "Failed")); }
  };
  return (
    <Card className="border-navy/5 shadow-none" data-testid="backups-card">
      <CardContent className="p-4 space-y-3">
        <h2 className="font-semibold text-navy flex items-center gap-2"><DatabaseBackup className="w-5 h-5 text-gold" /> Backups & recovery</h2>
        <p className="text-xs text-navy/60">Encrypted backup every night at 2am (UK). A 7am (UK) check re-runs the backup if the night's run failed or did not happen. Neon also keeps a 7-day restore history.</p>
        {health && (
          <div data-testid="backup-health" className={`rounded-lg px-3 py-2 text-xs ${health.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700 font-medium"}`}>
            Status: {health.ok ? "OK" : health.state.replace("_", " ").toUpperCase()} · Last good backup: {health.last_success_at ? stamp(health.last_success_at) : "none"}
            {health.last_error && <span className="block mt-1 break-words">Last error: {health.last_error}</span>}
            {health.tool_error && <span className="block mt-1 break-words">Tool error: {health.tool_error}</span>}
          </div>
        )}
        {store && (
          <p className="text-xs text-navy/60" data-testid="backup-storage-summary">
            {store.stored_backups} backups stored ({(store.stored_bytes / 1048576).toFixed(1)} MB), {store.purged_backups || 0} purged. {store.retention_note}
          </p>
        )}
        {isOwner && (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="min-h-[48px]" onClick={() => trigger("/admin/backups/run", "Backup started")} data-testid="run-backup-btn">Back up now</Button>
            <Button variant="outline" className="min-h-[48px]" onClick={() => trigger("/admin/restore-drill", "Restore drill started")} data-testid="run-drill-btn"><ShieldCheck className="w-4 h-4 mr-1" /> Test restore</Button>
          </div>
        )}
        <Button variant="ghost" className="w-full min-h-[44px] text-xs" onClick={load} data-testid="refresh-runs-btn">Refresh</Button>
        <ul data-testid="backup-runs">{runs.map((r) => <RunRow key={r.id} r={r} />)}</ul>
        {!runs.length && <p className="text-xs text-navy/40">No backups yet.</p>}
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { user, activeStoreId } = useAuth();
  const canSeeBackups = ["owner", "accountant"].includes(user?.role);
  return (
    <div className="space-y-4" data-testid="reports-page">
      <h1 className="text-2xl font-bold text-navy">Reports & backups</h1>
      <PackageCard storeId={activeStoreId} />
      {canSeeBackups && <BackupCard isOwner={user.role === "owner"} />}
    </div>
  );
}
